import React, { useState, useEffect } from "react";
import axios from "axios";
import { formatBRDate } from "../utils/date";
import { filterMembersForSession } from "../utils/masonicDegrees";
import { 
  CalendarCheck, 
  Plus, 
  Users, 
  HeartHandshake, 
  Check, 
  X, 
  Clock, 
  FileText, 
  Trash2, 
  Edit3, 
  Search,
  Sparkles,
  QrCode,
  Mail,
  Lock,
  Unlock,
  Send
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AttendanceSessions = ({
  sessions,
  members,
  onOpenNewSessionModal,
  onOpenEditSessionModal,
  onUpdateAttendance,
  onDeleteSession,
  onOpenQR,
  onOpenBoletim,
  onLockAttendance,
  onUnlockAttendance,
  boletimRefreshKey
}) => {
  const [selectedSession, setSelectedSession] = useState(sessions[0] || null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingBoletins, setPendingBoletins] = useState([]);
  const [dispatchingId, setDispatchingId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/boletins`, { params: { status_filter: "pendente" } });
        setPendingBoletins(res.data || []);
      } catch (err) {
        // silent — boletim endpoints may return 401 for unauthorized readers
      }
    };
    load();
  }, [boletimRefreshKey]);

  const boletimsForSelected = pendingBoletins.filter(b => selectedSession && b.session_id === selectedSession.id);
  const boletimsCountBySession = pendingBoletins.reduce((acc, b) => {
    acc[b.session_id] = (acc[b.session_id] || 0) + 1;
    return acc;
  }, {});

  const handleDispatch = async (bol) => {
    if (!window.confirm(`Despachar boletim para ${bol.recipient_member_ids?.length || 0} Irmão(s)?`)) return;
    setDispatchingId(bol.id);
    try {
      const res = await axios.post(`${API}/boletins/${bol.id}/dispatch`);
      toast.success(res.data.message);
      setPendingBoletins(prev => prev.filter(x => x.id !== bol.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao despachar boletim.");
    } finally {
      setDispatchingId(null);
    }
  };

  const handleCancelPendingBoletim = async (bol) => {
    if (!window.confirm("Cancelar este boletim pendente? Esta ação não pode ser desfeita.")) return;
    try {
      await axios.delete(`${API}/boletins/${bol.id}`);
      toast.success("Boletim pendente cancelado.");
      setPendingBoletins(prev => prev.filter(x => x.id !== bol.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cancelar boletim.");
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  };

  const handleToggleMemberAttendance = async (memberId, currentStatus) => {
    if (!selectedSession) return;
    if (selectedSession.attendance_locked) {
      toast.error("Livro de Presença trancado. Use 'Destravar' com justificativa para editar.");
      return;
    }

    let newPresent = [...(selectedSession.present_member_ids || [])];
    let newJustified = [...(selectedSession.justified_member_ids || [])];

    if (currentStatus === "present") {
      // Toggle to Justified
      newPresent = newPresent.filter(id => id !== memberId);
      newJustified.push(memberId);
    } else if (currentStatus === "justified") {
      // Toggle to Absent (Neither)
      newJustified = newJustified.filter(id => id !== memberId);
    } else {
      // Toggle to Present
      newPresent.push(memberId);
      newJustified = newJustified.filter(id => id !== memberId);
    }

    const updated = {
      ...selectedSession,
      present_member_ids: newPresent,
      justified_member_ids: newJustified
    };
    setSelectedSession(updated);

    try {
      await onUpdateAttendance(selectedSession.id, newPresent, newJustified);
      toast.success("Frequência atualizada em tempo real!");
    } catch (e) {
      toast.error("Erro ao salvar frequência.");
    }
  };

  const handleMarkAllPresent = async () => {
    if (!selectedSession) return;
    const allIds = members.map(m => m.id);
    setSelectedSession({
      ...selectedSession,
      present_member_ids: allIds,
      justified_member_ids: []
    });
    try {
      await onUpdateAttendance(selectedSession.id, allIds, []);
      toast.success("Todos os Irmãos foram marcados como Presentes!");
    } catch (e) {
      toast.error("Erro ao marcar presenças.");
    }
  };

  return (
    <div className="space-y-6" data-testid="attendance-sessions-section">
      
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-white">Frequência em Sessões Litúrgicas</h2>
          <p className="text-xs text-slate-400">
            Controle de chamada dos Irmãos, registro do Tronco de Beneficência e ata resumida
          </p>
        </div>

        <button
          data-testid="btn-open-new-session-modal"
          onClick={onOpenNewSessionModal}
          className="flex items-center space-x-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
        >
          <Plus className="w-4 h-4" />
          <span>Cadastrar Nova Sessão</span>
        </button>
      </div>

      {/* Main Split View: Sessions List + Interactive Attendance Sheet */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Sessions List */}
        <div className="luxury-card p-5 space-y-3">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="font-serif font-bold text-sm text-white">Livro de Presença (Sessões)</h3>
            <span className="text-xs font-mono text-[#D4AF37]">{sessions.length} registradas</span>
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {sessions.map((s) => {
              const isSelected = selectedSession?.id === s.id;
              const presentCount = s.present_member_ids?.length || 0;

              return (
                <div
                  key={s.id}
                  data-testid={`session-item-${s.id}`}
                  onClick={() => setSelectedSession(s)}
                  className={`p-3.5 rounded-xl cursor-pointer transition-all border ${
                    isSelected
                      ? "bg-[#162238] border-[#D4AF37] shadow-[0_0_15px_rgba(212,175,55,0.2)]"
                      : "bg-[#080C14] border-slate-800 hover:border-slate-700"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-[#D4AF37]">
                      Nº {s.session_number}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {formatBRDate(s.date)}
                    </span>
                  </div>

                  <h4 className="text-sm font-semibold text-white mt-1 line-clamp-1">{s.title}</h4>

                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-800/60 text-[11px]">
                    <span className="text-slate-400">{s.session_type}</span>
                    <div className="flex items-center gap-2">
                      {boletimsCountBySession[s.id] > 0 && (
                        <span
                          data-testid={`session-pending-boletim-badge-${s.id}`}
                          title={`${boletimsCountBySession[s.id]} boletim(ns) pendente(s) para despacho`}
                          className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40 font-bold flex items-center gap-1"
                        >
                          <Clock className="w-2.5 h-2.5" />
                          {boletimsCountBySession[s.id]}
                        </span>
                      )}
                      <span className="font-bold text-emerald-400">{presentCount} Obreiros</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column: Detailed Session Sheet & Interactive Member Attendance */}
        <div className="lg:col-span-2 space-y-5">
          {selectedSession ? (
            <div className="luxury-card p-6 space-y-6">
              
              {/* Session Overview Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-mono font-bold text-[#D4AF37] bg-[#D4AF37]/10 px-2.5 py-0.5 rounded-full border border-[#D4AF37]/30">
                      Sessão nº {selectedSession.session_number}
                    </span>
                    <span className="text-xs text-slate-400">
                      Data: {formatBRDate(selectedSession.date)}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30">
                      Grau: {selectedSession.degree_required}
                    </span>
                    {selectedSession.attendance_locked && (
                      <span
                        data-testid="badge-attendance-locked"
                        title={`Livro trancado em ${selectedSession.attendance_locked_at ? new Date(selectedSession.attendance_locked_at).toLocaleString('pt-BR') : ''}`}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-bold uppercase tracking-wider flex items-center gap-1"
                      >
                        <Lock className="w-3 h-3" />
                        Trancado
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mt-2">
                    {selectedSession.title}
                  </h3>
                </div>

                <div className="flex items-center space-x-2 flex-wrap gap-2">
                  <button
                    data-testid="session-btn-open-qr"
                    onClick={() => onOpenQR && onOpenQR(selectedSession)}
                    className="flex items-center space-x-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-[#D4AF37] border border-[#D4AF37]/40 font-semibold px-3 py-1.5 rounded-xl text-xs transition-all"
                    title="Gerar QR Code para presença"
                  >
                    <QrCode className="w-3.5 h-3.5" />
                    <span>QR de Presença</span>
                  </button>
                  <button
                    data-testid="session-btn-send-boletim"
                    onClick={() => onOpenBoletim && onOpenBoletim(selectedSession)}
                    className="flex items-center space-x-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-purple-300 border border-purple-500/40 font-semibold px-3 py-1.5 rounded-xl text-xs transition-all"
                    title="Enviar boletim aos Irmãos"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    <span>Enviar Boletim</span>
                  </button>
                  <button
                    onClick={handleMarkAllPresent}
                    disabled={selectedSession.attendance_locked}
                    className="bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-slate-950 font-bold px-3 py-1.5 rounded-xl text-xs transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Marcar Todos Presentes
                  </button>
                  {selectedSession.attendance_locked ? (
                    <button
                      data-testid="btn-unlock-attendance"
                      onClick={async () => {
                        const j = window.prompt("Este Livro está trancado. Informe a justificativa (mínimo 10 caracteres) para destravar. A alteração ficará registrada no histórico da Sessão:", "");
                        if (j === null) return;
                        if (!onUnlockAttendance) return;
                        const upd = await onUnlockAttendance(selectedSession.id, j);
                        if (upd) setSelectedSession(upd);
                      }}
                      className="flex items-center gap-1 bg-amber-500/15 border border-amber-500 text-amber-300 hover:bg-amber-500/25 font-bold px-3 py-1.5 rounded-xl text-xs transition-all"
                      title="Destravar Livro com Justificativa"
                    >
                      <Unlock className="w-3.5 h-3.5" />
                      Destravar
                    </button>
                  ) : (
                    <button
                      data-testid="btn-lock-attendance"
                      onClick={async () => {
                        if (!window.confirm("Salvar e TRANCAR o Livro de Presença desta Sessão?\n\nApós salvar, alterações só serão possíveis mediante justificativa formal registrada no histórico.")) return;
                        if (!onLockAttendance) return;
                        const upd = await onLockAttendance(selectedSession.id);
                        if (upd) setSelectedSession(upd);
                      }}
                      className="flex items-center gap-1 bg-emerald-500/15 border border-emerald-500 text-emerald-300 hover:bg-emerald-500/25 font-bold px-3 py-1.5 rounded-xl text-xs transition-all"
                      title="Salvar e Trancar (Chancelaria)"
                    >
                      <Lock className="w-3.5 h-3.5" />
                      Salvar &amp; Trancar
                    </button>
                  )}
                  <button
                    onClick={() => onDeleteSession(selectedSession)}
                    className="p-2 rounded-xl text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 border border-slate-800"
                    title="Excluir Sessão"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Session Metrics Bar: Tronco + Visitors + Agenda */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  data-testid="btn-edit-trunk-amount"
                  onClick={async () => {
                    const current = selectedSession.trunk_amount || 0;
                    const input = window.prompt(
                      `Valor arrecadado no Tronco de Beneficência\n(Sessão nº ${selectedSession.session_number || ""})\n\nDigite o novo valor em R$ (ponto ou vírgula para centavos):`,
                      String(current).replace(".", ",")
                    );
                    if (input === null) return;
                    const parsed = parseFloat(String(input).replace(",", "."));
                    if (isNaN(parsed) || parsed < 0) {
                      toast.error("Valor inválido. Use ex.: 250,50 ou 250.50.");
                      return;
                    }
                    try {
                      const res = await axios.patch(`${API}/sessions/${selectedSession.id}/trunk-amount`, { trunk_amount: parsed });
                      toast.success(res.data.message || "Tronco atualizado.");
                      // Refresh selected session locally
                      setSelectedSession({ ...selectedSession, trunk_amount: res.data.session?.trunk_amount ?? parsed });
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Erro ao atualizar o Tronco.");
                    }
                  }}
                  className="p-3 rounded-xl bg-[#080C14] border border-slate-800 hover:border-[#D4AF37]/50 text-left transition-all group"
                  title="Clique para editar o valor arrecadado no Tronco"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Tronco de Beneficência</span>
                    <Edit3 className="w-3 h-3 text-slate-600 group-hover:text-[#D4AF37]" />
                  </div>
                  <span className="font-mono font-bold text-base text-[#D4AF37] block mt-1">
                    {formatCurrency(selectedSession.trunk_amount)}
                  </span>
                </button>

                <div className="p-3 rounded-xl bg-[#080C14] border border-slate-800">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Tipo Ritualístico</span>
                  <span className="font-semibold text-xs text-white block truncate">
                    {selectedSession.session_type}
                  </span>
                </div>

                <div className="p-3 rounded-xl bg-[#080C14] border border-slate-800">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Visitantes Registrados</span>
                  <span className="text-xs text-slate-300 block truncate">
                    {selectedSession.visitors || "Nenhum visitante"}
                  </span>
                </div>
              </div>

              {selectedSession.agenda && (
                <div className="p-3 rounded-xl bg-[#080C14]/80 border border-slate-800 text-xs">
                  <span className="text-slate-400 font-semibold block mb-1">Pauta / Ordem dos Trabalhos:</span>
                  <p className="text-slate-300">{selectedSession.agenda}</p>
                </div>
              )}

              {/* Boletins Pendentes de Despacho */}
              {boletimsForSelected.length > 0 && (
                <div
                  data-testid="pending-boletins-section"
                  className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/30 space-y-3"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <h4 className="font-serif font-bold text-sm text-amber-300 uppercase tracking-wider">
                      Boletins Pendentes de Despacho ({boletimsForSelected.length})
                    </h4>
                  </div>
                  <p className="text-[11px] text-slate-400 italic">
                    Rascunhos criados pela Secretaria aguardando envio. Despache quando o SMTP estiver pronto ou cancele se não for mais necessário.
                  </p>
                  <div className="space-y-2">
                    {boletimsForSelected.map((bol) => (
                      <div
                        key={bol.id}
                        data-testid={`pending-boletim-${bol.id}`}
                        className="flex items-start justify-between gap-3 p-3 rounded-lg bg-[#0B111E] border border-amber-500/20"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-white font-semibold truncate">{bol.subject || "(Sem assunto)"}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {bol.recipient_member_ids?.length || 0} destinatário(s) • criado {new Date(bol.created_at).toLocaleString("pt-BR")}
                          </div>
                          {bol.custom_message && (
                            <div className="text-[11px] text-slate-300 mt-1.5 line-clamp-2 italic">"{bol.custom_message}"</div>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            data-testid={`boletim-dispatch-${bol.id}`}
                            onClick={() => handleDispatch(bol)}
                            disabled={dispatchingId === bol.id}
                            className="flex items-center gap-1 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/50 text-emerald-300 font-bold px-2.5 py-1 rounded-lg text-[10px] uppercase tracking-wider disabled:opacity-40"
                            title="Despachar agora (envia e-mails)"
                          >
                            <Send className="w-3 h-3" />
                            {dispatchingId === bol.id ? "..." : "Despachar"}
                          </button>
                          <button
                            data-testid={`boletim-cancel-${bol.id}`}
                            onClick={() => handleCancelPendingBoletim(bol)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 border border-slate-800"
                            title="Cancelar rascunho"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interactive Call Sheet (Lista de Chamada) */}
              <div>
                {(() => {
                  const eligibleMembers = filterMembersForSession(members, selectedSession.degree_required, selectedSession.date);
                  const presentCount = (selectedSession.present_member_ids || []).filter((id) => eligibleMembers.some((m) => m.id === id)).length;
                  return (
                <>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-serif font-bold text-sm text-white">
                    Chamada Nominal dos Irmãos ({presentCount} de {eligibleMembers.length} presentes)
                  </h4>
                  <div className="flex items-center space-x-3 text-xs text-slate-400">
                    <span className="flex items-center space-x-1">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
                      <span>Presente</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                      <span>Justificado</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <span className="w-2.5 h-2.5 rounded-full bg-slate-600"></span>
                      <span>Falta</span>
                    </span>
                  </div>
                </div>

                <p className="text-[10px] text-slate-500 italic mb-2">
                  Regra maçônica: Sessão de <b>{selectedSession.degree_required}</b> — Irmãos deste grau e superiores.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[380px] overflow-y-auto pr-1">
                  {eligibleMembers.map((m) => {
                    const isPresent = selectedSession.present_member_ids?.includes(m.id);
                    const isJustified = selectedSession.justified_member_ids?.includes(m.id);
                    const statusType = isPresent ? "present" : isJustified ? "justified" : "absent";

                    return (
                      <div
                        key={m.id}
                        data-testid={`attendance-member-${m.id}`}
                        onClick={() => handleToggleMemberAttendance(m.id, statusType)}
                        className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                          isPresent
                            ? "bg-emerald-500/10 border-emerald-500/40 text-white"
                            : isJustified
                            ? "bg-amber-500/10 border-amber-500/40 text-slate-200"
                            : "bg-[#080C14] border-slate-800 text-slate-400 hover:border-slate-700"
                        }`}
                      >
                        <div>
                          <span className="font-semibold text-xs block leading-tight">{m.name}</span>
                          <span className="text-[10px] text-slate-400 font-mono">
                            CIM: {m.cim} • {m.degree}
                          </span>
                        </div>

                        <div>
                          {isPresent && (
                            <span className="flex items-center space-x-1 text-[10px] font-bold text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded-full">
                              <Check className="w-3 h-3" />
                              <span>Presente</span>
                            </span>
                          )}
                          {isJustified && (
                            <span className="flex items-center space-x-1 text-[10px] font-bold text-amber-400 bg-amber-500/20 px-2 py-0.5 rounded-full">
                              <Clock className="w-3 h-3" />
                              <span>Justificado</span>
                            </span>
                          )}
                          {!isPresent && !isJustified && (
                            <span className="text-[10px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
                              Ausente
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                </>
                  );
                })()}
              </div>

            </div>
          ) : (
            <div className="luxury-card p-12 text-center text-slate-400 text-xs">
              Selecione uma sessão ou cadastre uma nova sessão litúrgica.
            </div>
          )}
        </div>

      </div>

    </div>
  );
};
